import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  School as SchoolIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  InfoOutlined
} from '@mui/icons-material';

// Import tab components
import MyCoursesTab from './Components/MyCoursesTab';
import MyPaymentsTab from './Components/MyPaymentsTab';
import RenewCourseTab from './Components/RenewCourseTab';
import { API_URL, createAxiosInstance } from '../../api';

const FREE_ADVERTS_CAP = 2;

const MyCourses = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1020px)');
  const location = useLocation();
  const userId = useSelector((state) => state.main.userId);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Premium free-advert allowance — drives the subtitle, the top-right
  // counter card, and the banner inside the courses tab. Backed by the
  // existing /api/billings/free-advert-eligibility endpoint, which returns
  // { eligible, reason, remaining } and treats reason === "not_premium"
  // as the Basic-tier signal.
  const [freeAdvert, setFreeAdvert] = useState({
    loading: true,
    isPremium: false,
    used: 0,
    cap: FREE_ADVERTS_CAP,
    remaining: 0,
  });

  useEffect(() => {
    const fetchEligibility = async () => {
      if (!userId) return;
      try {
        const axiosInstance = createAxiosInstance();
        const res = await axiosInstance.get(
          `${API_URL}/api/billings/free-advert-eligibility/${userId}`
        );
        const { reason, remaining } = res.data || {};
        const isPremium = reason !== 'not_premium';
        const remainingSafe = typeof remaining === 'number' ? remaining : 0;
        const used = Math.max(0, FREE_ADVERTS_CAP - remainingSafe);
        setFreeAdvert({
          loading: false,
          isPremium,
          used,
          cap: FREE_ADVERTS_CAP,
          remaining: remainingSafe,
        });
      } catch (e) {
        // Endpoint is best-effort UX. If it fails we collapse the widgets
        // rather than show stale or wrong counts.
        console.warn('Free-advert eligibility check failed:', e?.message);
        setFreeAdvert((s) => ({ ...s, loading: false }));
      }
    };
    fetchEligibility();
  }, [userId]);

  // Tab configuration
  const tabs = useMemo(() => [
    {
      label: 'My Courses',
      icon: <SchoolIcon />,
      component: MyCoursesTab
    },
    {
      label: 'My Payments',
      icon: <PaymentIcon />,
      component: MyPaymentsTab
    },
    {
      label: 'Renew Course',
      icon: <RefreshIcon />,
      component: RenewCourseTab
    }
  ], []);

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // Get current tab component
  const CurrentTabComponent = tabs[activeTab]?.component;

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2, 
      maxWidth: '100%'
    }}>
      {/* Page Title + Premium free-advert counter.
          The counter card on the right and the subtitle under the H1 are
          only shown when the tutor is on Premium — Basic users see the
          plain heading. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: freeAdvert.isPremium ? 0.5 : 0 }}>
            My Courses
          </Typography>
          {freeAdvert.isPremium && (
            <Typography variant="body2" color="text.secondary">
              Advertise up to <strong>{freeAdvert.cap}</strong> courses per year with your Premium plan.
            </Typography>
          )}
        </Box>

        {freeAdvert.isPremium && (
          <Box
            sx={{
              minWidth: 240,
              border: '1px solid #e4d6ec',
              borderRadius: 2,
              p: 2,
              backgroundColor: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#712d94', fontWeight: 600 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {freeAdvert.used} / {freeAdvert.cap}
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  courses used
                </Box>
              </Typography>
              <Tooltip
                arrow
                title="Premium tutors can publish up to 2 course adverts per rolling 365 days at no extra cost."
              >
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(freeAdvert.used / freeAdvert.cap) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: '#f5f0f7',
                '& .MuiLinearProgress-bar': { backgroundColor: '#712d94' },
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              {freeAdvert.remaining} remaining
            </Typography>
          </Box>
        )}
      </Box>

      {/* Horizontal Tabs */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'white',
        borderRadius: 1,
        px: 2
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3
            },
            '& .MuiTab-root': {
              minHeight: '48px',
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderRadius: 1,
        minHeight: '400px'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '400px',
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              Loading course information...
            </Typography>
          </Box>
        ) : CurrentTabComponent ? (
          <CurrentTabComponent freeAdvert={freeAdvert} />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '400px',
            gap: 2,
            p: 3
          }}>
            <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              No Courses Available
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Start by creating your first course.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

MyCourses.displayName = 'MyCourses';

export default MyCourses;
